import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar } from "../components/ui/Avatar";
import { PartyCard } from "../components/ui/PartyCard";
import { apiClient } from "../lib/api";
import type { Party } from "../lib/types";

interface PublicProfile {
  id: number;
  name: string;
  avatar_url: string | null;
  created_at: string;
  parties: Party[];
  voted_parties: Party[];
  // Present only when viewing own profile
  drafts?: Party[];
  vote_credits?: number;
  total_vote_credits?: number;
}

export function UserProfile() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const _navigate = useNavigate();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setNotFound(false);
    apiClient
      .get<PublicProfile>(`/api/users/${id}`)
      .then(setProfile)
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <main className="min-h-screen bg-stone-50 dark:bg-stone-950 pt-14 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
      </main>
    );
  }

  if (notFound || !profile) {
    return (
      <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14 flex items-center justify-center">
        <p className="text-stone-500 text-sm">{t("errors.userNotFound")}</p>
      </main>
    );
  }

  const isOwnProfile = profile.vote_credits !== undefined;

  return (
    <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14">
      <div className="max-w-2xl mx-auto px-4 py-12 flex flex-col gap-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Avatar name={profile.name} imageUrl={profile.avatar_url} size={64} />
          <div>
            <h1 className="text-2xl font-bold">{profile.name}</h1>
            <p className="text-stone-500 text-sm">
              {t("profile.published", { count: profile.parties.length })}
            </p>
          </div>
        </div>

        {/* Own-profile: vote credit info */}
        {isOwnProfile && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-3 text-xs text-stone-600 dark:text-stone-400 flex flex-col gap-1">
            <p className="font-semibold text-stone-700 dark:text-stone-300 mb-0.5">
              {t("profile.howVotingWorks")}
            </p>
            <p>{t("profile.votingRule1")}</p>
            <p>
              · Publish a party to earn{" "}
              <span className="text-amber-600 dark:text-amber-400 font-medium">
                +2 votes
              </span>
              .
            </p>
            <p>{t("profile.votingRule3")}</p>
            <p className="mt-1 font-medium text-stone-700 dark:text-stone-300">
              {t("profile.votesUsed", {
                used:
                  (profile.total_vote_credits ?? 0) -
                  (profile.vote_credits ?? 0),
                total: profile.total_vote_credits ?? 0,
              })}
            </p>
          </div>
        )}

        {/* Own-profile: draft parties */}
        {isOwnProfile && (profile.drafts?.length ?? 0) > 0 && (
          <section>
            <h2 className="text-stone-500 dark:text-stone-400 text-sm font-semibold uppercase tracking-wider mb-4">
              {t("profile.drafts")}
            </h2>
            <div className="flex flex-col gap-3">
              {profile.drafts?.map((party) => (
                <PartyCard
                  key={party.id}
                  party={party}
                  showHost={false}
                  to={`/build/${party.slug}`}
                  subtitle={t("card.guest", { count: party.guests.length })}
                  badge={
                    <span className="bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                      {t("card.draft")}
                    </span>
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* Published parties */}
        <section>
          <h2 className="text-stone-500 dark:text-stone-400 text-sm font-semibold uppercase tracking-wider mb-4">
            {isOwnProfile
              ? t("profile.publishedParties")
              : t("profile.parties")}
          </h2>
          {profile.parties.length === 0 ? (
            <p className="text-stone-400 dark:text-stone-600 text-sm">
              {isOwnProfile ? (
                <>
                  {t("profile.noPublished")}{" "}
                  <Link
                    to="/build"
                    className="text-amber-500 hover:text-amber-400"
                  >
                    {t("profile.buildOne")}
                  </Link>
                </>
              ) : (
                t("profile.noPublished")
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {profile.parties.map((party) => (
                <PartyCard key={party.id} party={party} showHost={false} />
              ))}
            </div>
          )}
        </section>

        {/* Votes cast */}
        <section>
          <h2 className="text-stone-500 dark:text-stone-400 text-sm font-semibold uppercase tracking-wider mb-4">
            {t("profile.votesCast")}
          </h2>
          {profile.voted_parties.length === 0 ? (
            <p className="text-stone-400 dark:text-stone-600 text-sm">
              {isOwnProfile ? (
                <>
                  {t("profile.noVotes")}{" "}
                  <Link to="/" className="text-amber-500 hover:text-amber-400">
                    {t("profile.browseParties")}
                  </Link>
                </>
              ) : (
                t("profile.noVotes")
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {profile.voted_parties.map((party) => (
                <PartyCard key={party.id} party={party} />
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
